import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Copy, EyeOff } from 'lucide-react';
import { cn } from 'lib/utils';
import { DESK_NAME } from 'api/sectorAlignmentApi';
import type { EcmIssue, EcmUser, SectorDesk } from 'api/sectorAlignmentApi';
import { ECM_GRID, list, type Tagged } from './rows';

const LINK = 'inline-flex items-center gap-1 text-[13px] font-medium underline underline-offset-[3px]';

// AC10: links only, the page corrects nothing. The users list reads `?search=` (desk name or email).
const DESK_FULL_NAME: Record<SectorDesk, string> = { 3: "L'OREAL LUXE", 205: "L'OREAL DPGP" }; // backend getServiceDeskName
const usersOfDesk = (sd: SectorDesk) => `/admin?search=${encodeURIComponent(DESK_FULL_NAME[sd])}`;
const userRecord = (user: EcmUser | undefined) => `/admin?search=${encodeURIComponent(user?.email || user?.name || '')}`;
const names = (users: EcmUser[] | null | undefined) => list(users).map((u) => u.name).join(', ');

interface Cells { subject: string; prefix?: string; problem: string; extra?: string; action: ReactNode; severe?: boolean }

const EcmRow = ({ issue }: { issue: Tagged<EcmIssue> }) => {
  const { t } = useTranslation();
  const deskLink = <Link to={usersOfDesk(issue.sd)} className={LINK}>{t("See the desk's users")}</Link>;
  const recordLink = (user: EcmUser | undefined) => (
    <Link to={userRecord(user)} className={LINK}>
      {t('Open the record')}
    </Link>
  );
  const cells: Cells = (() => {
    switch (issue.kind) {
      case 'sector-without-ecm':
        return { subject: issue.sector, prefix: t('{{count}} ticket', { count: Number(issue.tickets) || 0 }), problem: t('No ECM attached: these tickets are visible to no one'), action: deskLink, severe: true };
      case 'unknown-sector':
        return { subject: issue.user?.name, prefix: issue.sector, problem: t('Sector unknown in Jira'), extra: issue.suggestion ?? undefined, action: recordLink(issue.user) };
      case 'no-sector':
        return { subject: issue.user?.name, problem: t('No sector'), action: recordLink(issue.user) };
      case 'multiple-sectors':
        return { subject: issue.user?.name, prefix: (issue.user?.sectors ?? []).join(', '), problem: t('Several sectors'), action: recordLink(issue.user) };
      case 'shared-sector':
        return { subject: issue.sector, prefix: names(issue.users), problem: t('Sector shared by {{count}} ECMs', { count: issue.users?.length ?? 0 }), action: deskLink };
      default:
        return { subject: '—', problem: '—', action: null };
    }
  })();

  return (
    <div role="row" className={cn(ECM_GRID, 'h-[52px] border-b last:border-b-0')}>
      <span role="cell" className="flex min-w-0 items-center gap-2">
        {cells.severe && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-rose-500" aria-hidden="true" />}
        <span className="truncate text-[13.5px] font-medium" title={cells.subject}>{cells.subject || '—'}</span>
      </span>
      <span role="cell" className="text-[13px] text-muted-foreground">{DESK_NAME[issue.sd]}</span>
      <span role="cell" className="flex min-w-0 items-center gap-1.5 text-[13px]">
        {cells.severe && <EyeOff className="h-[15px] w-[15px] shrink-0 text-rose-600 dark:text-rose-400" aria-hidden="true" />}
        <span className={cn('truncate', cells.severe && 'font-medium text-rose-700 dark:text-rose-400')}>
          {cells.prefix && <><span className="text-muted-foreground">{cells.prefix}</span><span className="text-zinc-400 dark:text-zinc-500"> · </span></>}
          {cells.problem}
          {cells.extra && <> · {t('Close sector:')} <span className="font-medium">{cells.extra}</span></>}
        </span>
        {cells.extra && (
          <button
            type="button"
            aria-label={t('Copy {{value}}', { value: cells.extra })}
            onClick={() => navigator.clipboard?.writeText(cells.extra ?? '').catch(() => undefined)}
            className="shrink-0 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Copy className="h-[13px] w-[13px]" aria-hidden="true" />
          </button>
        )}
      </span>
      <span role="cell" className="text-right">{cells.action}</span>
    </div>
  );
};

export default EcmRow;
